// Guarded initializeApp() lives here (and mirrors packages/store/src/firebase.ts)
// so this module is self-sufficient: previously it imported `auth` from the store
// and called getApp() at module top-level, relying on the store's chunk to have
// evaluated its init side-effect first. With production chunk-splitting that
// ordering is not guaranteed — getApp() ran before initializeApp() and threw
// `auth/no-app`. The window.__factoriFirebase guard ensures only one app
// instance across host + store.
import { initializeApp, getApp, getApps } from 'firebase/app';
import { GoogleAuthProvider, getAuth, signInWithPopup, type Auth } from 'firebase/auth';
import { getDatabase, type Database } from 'firebase/database';
import { getRemoteConfig } from 'firebase/remote-config';

type FirebaseBridge = { auth: Auth; database: Database };
const WIN = window as Window & { __factoriFirebase?: FirebaseBridge };

if (!WIN.__factoriFirebase) {
  const firebaseConfig = {
    apiKey:      import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain:  import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId:   import.meta.env.VITE_FIREBASE_PROJECT_ID,
    appId:       import.meta.env.VITE_FIREBASE_APP_ID,
    databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  };
  const initialized = getApps().length ? getApp() : initializeApp(firebaseConfig);
  WIN.__factoriFirebase = { auth: getAuth(initialized), database: getDatabase(initialized) };
}

const auth = WIN.__factoriFirebase.auth;
const app = getApp();

const googleAuthProvider = new GoogleAuthProvider();
// _app alias kept for any callers that used the old export name
const _app = auth;

const remoteConfig = getRemoteConfig(app);
remoteConfig.settings.minimumFetchIntervalMillis = 3_600_000;

const awaitFirebaseInitialization = () =>
  new Promise((resolve, reject) => {
    const unsubscribe = auth.onAuthStateChanged(userAuth => {
      unsubscribe();
      resolve(userAuth);
    }, reject);
  });

export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleAuthProvider);
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const token = credential?.accessToken;
    const user = result.user;
    return { user, token };
  } catch (error) {
    console.error('Error during Google sign-in: ', error);
    throw error;
  }
};

export { auth, googleAuthProvider, awaitFirebaseInitialization, remoteConfig, _app, app };
